/**
 * Tidying a group of hand-placed boxes.
 *
 * Alignment answers "put these on one line"; distribution answers "put equal
 * air between these". Both are pure arithmetic over the boxes as they are
 * currently DRAWN, so a box the tidy tree placed can be aligned without first
 * having been dragged: the caller turns the result into stored coordinates.
 */

export type AlignmentAction =
  | 'align-left'
  | 'align-center'
  | 'align-right'
  | 'distribute-horizontal'
  | 'distribute-vertical'

export interface AlignmentBox {
  id: number
  x: number
  y: number
  width: number
  height: number
}

export interface AlignedPosition {
  id: number
  x: number
  y: number
}

/** How each action is named in tooltips, notices and undo history. */
export const ALIGNMENT_LABELS: Record<AlignmentAction, string> = {
  'align-left': 'align left',
  'align-center': 'align centre',
  'align-right': 'align right',
  'distribute-horizontal': 'distribute horizontally',
  'distribute-vertical': 'distribute vertically',
}

/** Spreading boxes evenly needs a middle to move: two boxes are already even. */
export const MIN_DISTRIBUTE_BOXES = 3

const round = (value: number) => Math.round(value * 100) / 100

function distribute(boxes: AlignmentBox[], axis: 'x' | 'y'): AlignedPosition[] {
  if (boxes.length < MIN_DISTRIBUTE_BOXES) return []
  const size = axis === 'x' ? 'width' : 'height'
  const ordered = [...boxes].sort((a, b) => a[axis] - b[axis] || a.id - b.id)
  const last = ordered[ordered.length - 1]
  // The outer two boxes are the span; only the ones between them move.
  const span = last[axis] + last[size] - ordered[0][axis]
  const occupied = ordered.reduce((total, box) => total + box[size], 0)
  const gap = (span - occupied) / (ordered.length - 1)

  let cursor = ordered[0][axis]
  return ordered.map(box => {
    const placed = cursor
    cursor += box[size] + gap
    return axis === 'x'
      ? { id: box.id, x: round(placed), y: round(box.y) }
      : { id: box.id, x: round(box.x), y: round(placed) }
  })
}

/**
 * Where each box ends up. Returns an empty list when the action cannot mean
 * anything for this selection, so a caller can say so rather than saving a
 * request that changes nothing.
 */
export function applyAlignment(boxes: AlignmentBox[], action: AlignmentAction): AlignedPosition[] {
  if (boxes.length < 2) return []

  switch (action) {
    case 'align-left': {
      const left = Math.min(...boxes.map(box => box.x))
      return boxes.map(box => ({ id: box.id, x: round(left), y: round(box.y) }))
    }
    case 'align-right': {
      const right = Math.max(...boxes.map(box => box.x + box.width))
      return boxes.map(box => ({ id: box.id, x: round(right - box.width), y: round(box.y) }))
    }
    case 'align-center': {
      const centres = boxes.map(box => box.x + box.width / 2)
      // The midpoint of the outer edges, not the mean of the centres: the mean
      // drifts towards whichever side happens to hold more boxes.
      const centre = (Math.min(...centres) + Math.max(...centres)) / 2
      return boxes.map(box => ({ id: box.id, x: round(centre - box.width / 2), y: round(box.y) }))
    }
    case 'distribute-horizontal':
      return distribute(boxes, 'x')
    case 'distribute-vertical':
      return distribute(boxes, 'y')
    default:
      return []
  }
}

/** The screen rectangle a set of boxes occupies, or null when there is none. */
export function boundingBox(boxes: AlignmentBox[]) {
  if (boxes.length === 0) return null
  return {
    x: Math.min(...boxes.map(box => box.x)),
    y: Math.min(...boxes.map(box => box.y)),
    right: Math.max(...boxes.map(box => box.x + box.width)),
    bottom: Math.max(...boxes.map(box => box.y + box.height)),
  }
}
